import { useEffect, useRef, useState } from 'react';
import { api, formatErr, API } from '@/lib/api';
import { PageHeader, PageShell } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Briefcase, MapPin, IndianRupee, Users2, FileText, Upload, X, CheckCircle2, PauseCircle, Pencil } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const STATUS_COLOR = { open: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30', on_hold: 'bg-amber-500/15 text-amber-600 border-amber-500/30', closed: 'bg-zinc-500/15 text-zinc-500 border-zinc-500/30' };
const PRIORITY_COLOR = { high: 'text-red-500', medium: 'text-amber-500', low: 'text-muted-foreground' };

const EMPTY = { title: '', client: '', location: '', experience: '', positions: 1, ctc: '', skills: '', assigned_to: [], status: 'open', priority: 'medium', jd_path: '', jd_filename: '' };

export default function JobOpenings() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [filter, setFilter] = useState('all');
  const [jdFile, setJdFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const canManage = ['admin', 'manager'].includes(user?.role);

  const load = async () => {
    try {
      const params = filter === 'all' ? {} : { status_filter: filter };
      const { data } = await api.get('/jobs', { params });
      setRows(data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => { api.get('/users').then(({ data }) => setUsers(data)).catch(() => {}); }, []);

  const save = async () => {
    if (!form.title || !form.client) { toast.error('Title and client required'); return; }
    try {
      const payload = { ...form, positions: parseInt(form.positions || 1, 10) };
      let jobId = editingId;
      if (editingId) {
        const { data } = await api.patch(`/jobs/${editingId}`, payload);
        jobId = data.id;
      } else {
        const { data: created } = await api.post('/jobs', payload);
        jobId = created.id;
      }
      if (jdFile) {
        setUploading(true);
        const fd = new FormData(); fd.append('file', jdFile);
        try {
          await api.post(`/jobs/${jobId}/upload-jd`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch (e) { toast.error('JD upload failed: ' + formatErr(e)); }
        setUploading(false);
      }
      toast.success(editingId ? 'Job updated' : 'Job created');
      setOpen(false); setForm(EMPTY); setJdFile(null); setEditingId(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const openEdit = (j) => {
    setEditingId(j.id);
    setForm({
      title: j.title || '', client: j.client || '', location: j.location || '', experience: j.experience || '',
      positions: j.positions || 1, ctc: j.ctc || '', skills: j.skills || '',
      assigned_to: j.assigned_to || [], status: j.status || 'open', priority: j.priority || 'medium',
      jd_path: j.jd_path || '', jd_filename: j.jd_filename || '',
    });
    setJdFile(null); setOpen(true);
  };

  const changeStatus = async (job, status) => {
    try {
      await api.patch(`/jobs/${job.id}`, { ...job, status });
      toast.success(status === 'closed' ? 'Job closed' : status === 'on_hold' ? 'Put on hold' : 'Reopened');
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const toggleAssignee = (uid) => setForm(f => ({
    ...f, assigned_to: f.assigned_to.includes(uid) ? f.assigned_to.filter(x => x !== uid) : [...f.assigned_to, uid]
  }));

  const nameOf = (uid) => users.find(u => u.id === uid)?.name || 'Unknown';

  const filteredCount = {
    open: rows.filter(r => r.status === 'open').length,
    on_hold: rows.filter(r => r.status === 'on_hold').length,
    closed: rows.filter(r => r.status === 'closed').length,
  };

  const token = localStorage.getItem('rh_token');
  const jdUrl = (path) => `${API}/files/${path}?auth=${encodeURIComponent(token || '')}`;

  const downloadJd = async (job) => {
    try {
      const res = await api.get(`/files/${job.jd_path}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = job.jd_filename || 'jd';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <PageShell>
      <PageHeader
        eyebrow="Requisitions"
        title="Job openings"
        subtitle="Every mandate your agency is working on. Attach the JD, assign recruiters, and close it once filled."
        actions={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(EMPTY); setJdFile(null); setEditingId(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="new-job-btn" className="bg-primary hover:bg-primary/90 text-white"><Plus className="w-4 h-4 mr-2" />New Opening</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-2xl font-black">{editingId ? 'Edit job opening' : 'Add job opening'}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Job title</Label><Input data-testid="job-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                <div><Label>Client</Label><Input data-testid="job-client" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} /></div>
                <div><Label>Location</Label><Input data-testid="job-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Bengaluru / Remote" /></div>
                <div><Label>Experience</Label><Input value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} placeholder="3-5 yrs" /></div>
                <div><Label>CTC</Label><Input value={form.ctc} onChange={(e) => setForm({ ...form, ctc: e.target.value })} placeholder="10-15 LPA" /></div>
                <div><Label>Positions</Label><Input type="number" min="1" value={form.positions} onChange={(e) => setForm({ ...form, positions: parseInt(e.target.value || 1, 10) })} /></div>
                <div>
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Assign to (multi-select)</Label>
                  <div className="mt-2 border border-border/70 rounded-md p-3 max-h-40 overflow-y-auto space-y-2" data-testid="assign-list">
                    {users.length === 0 && <div className="text-xs text-muted-foreground">No teammates yet — add them in the Team panel first.</div>}
                    {users.map(u => (
                      <label key={u.id} className="flex items-center gap-3 cursor-pointer text-sm">
                        <Checkbox checked={form.assigned_to.includes(u.id)} onCheckedChange={() => toggleAssignee(u.id)} data-testid={`assign-${u.id}`} />
                        <span className="flex-1">{u.name}</span>
                        <Badge variant="outline" className="text-[10px] uppercase">{u.role}</Badge>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="col-span-2"><Label>Skills / notes</Label><Textarea rows={2} value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} /></div>
                <div className="col-span-2">
                  <Label>Attach JD (PDF/DOC/DOCX/TXT — any format)</Label>
                  <div className="mt-1 flex items-center gap-2">
                    <Input data-testid="jd-file" type="file" onChange={(e) => setJdFile(e.target.files?.[0] || null)} className="cursor-pointer" />
                    {jdFile && <Button type="button" size="icon" variant="ghost" onClick={() => setJdFile(null)}><X className="w-4 h-4" /></Button>}
                  </div>
                  {jdFile && <div className="text-xs text-muted-foreground mt-1">{jdFile.name} · {(jdFile.size / 1024).toFixed(1)} KB</div>}
                </div>
                <Button data-testid="job-save" onClick={save} disabled={uploading} className="col-span-2 bg-primary hover:bg-primary/90 text-white">
                  {uploading ? 'Uploading JD…' : (editingId ? 'Save changes' : 'Save opening')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="flex items-center gap-2 mb-4">
        {[['all', 'All'], ['open', `Open (${filteredCount.open})`], ['on_hold', `On Hold (${filteredCount.on_hold})`], ['closed', `Closed (${filteredCount.closed})`]].map(([k, l]) => (
          <button key={k} data-testid={`job-filter-${k}`} onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-md text-xs uppercase tracking-widest border transition-colors ${filter === k ? 'border-primary bg-primary text-white' : 'border-border/70 hover:border-primary'}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="jobs-grid">
        {rows.map(j => (
          <div key={j.id} className="tactical-card p-5 hover:-translate-y-0.5 transition-transform flex flex-col">
            <div className="flex items-start justify-between mb-3">
              <Badge variant="outline" className={STATUS_COLOR[j.status]}>{j.status.replace('_', ' ')}</Badge>
              <div className={`text-[10px] uppercase tracking-widest font-bold ${PRIORITY_COLOR[j.priority]}`}>● {j.priority}</div>
            </div>
            <div className="font-display text-xl font-black tracking-tight leading-tight">{j.title}</div>
            <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1"><Briefcase className="w-3 h-3" /> {j.client}</div>
            <div className="mt-3 space-y-1.5 text-xs text-muted-foreground">
              <div className="flex items-center gap-2"><MapPin className="w-3 h-3" /> {j.location || '—'} · {j.experience || '—'}</div>
              <div className="flex items-center gap-2"><IndianRupee className="w-3 h-3" /> {j.ctc || '—'}</div>
              <div className="flex items-center gap-2"><Users2 className="w-3 h-3" /> {j.positions} position{j.positions > 1 ? 's' : ''}</div>
            </div>
            {j.assigned_to && j.assigned_to.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {j.assigned_to.map(uid => (
                  <Badge key={uid} variant="secondary" className="text-[10px]">{nameOf(uid)}</Badge>
                ))}
              </div>
            )}
            {j.skills && <div className="mt-3 pt-3 border-t border-border/70 text-xs">{j.skills}</div>}
            <div className="mt-auto pt-3 border-t border-border/70 flex items-center gap-2 flex-wrap">
              {j.jd_path ? (
                <Button variant="outline" size="sm" onClick={() => downloadJd(j)} data-testid={`jd-view-${j.id}`}>
                  <FileText className="w-3 h-3 mr-1" /> View JD
                </Button>
              ) : canManage ? (
                <UploadJdBtn job={j} onDone={load} />
              ) : null}
              {canManage && j.status !== 'closed' && (
                <Button variant="outline" size="sm" onClick={() => changeStatus(j, 'closed')} data-testid={`close-${j.id}`} className="text-red-500 border-red-500/40 hover:bg-red-500 hover:text-white">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Close
                </Button>
              )}
              {canManage && j.status === 'closed' && (
                <Button variant="outline" size="sm" onClick={() => changeStatus(j, 'open')}>Reopen</Button>
              )}
              {canManage && j.status === 'open' && (
                <Button variant="ghost" size="sm" onClick={() => changeStatus(j, 'on_hold')}><PauseCircle className="w-3 h-3 mr-1" />Hold</Button>
              )}
              {canManage && (
                <Button variant="ghost" size="sm" onClick={() => openEdit(j)} data-testid={`edit-job-${j.id}`}>
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
              )}
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="col-span-full text-center text-muted-foreground p-12 tactical-card">No openings yet. Click New Opening to add your first mandate.</div>}
      </div>
    </PageShell>
  );
}

function UploadJdBtn({ job, onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      await api.post(`/jobs/${job.id}/upload-jd`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('JD uploaded');
      onDone();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => ref.current?.click()} disabled={busy} data-testid={`jd-upload-${job.id}`}>
        <Upload className="w-3 h-3 mr-1" /> {busy ? 'Uploading…' : 'Attach JD'}
      </Button>
      <input ref={ref} type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
    </>
  );
}
